"use client";

import React, { useRef, useState, useEffect, useCallback } from "react";
import {Box, Stack, List, ListItem, TextField, ClickAwayListener} from "@mui/material";
import {
  GroupedResults,
  SearchSuggestions,
  SuggestionType,
  Document
} from "@/types/search/suggestions-search-response";
import { Locale, localeItems } from "@/i18n/routing";
import { useRouter } from "next/navigation";
import { LocaleItem } from "@/types/contentful/locale-item";
import {Close, Search} from "@mui/icons-material";
import styles from "./styles.module.css";
import SearchSuggestionResultItem from "./search-suggestion-result-item";

interface DocGroup {
  title: string;
  docs: Document[];
}

export default function SearchPredictive({ locale }: { locale: Locale }) {
  const [suggestionsData, setData] = useState<DocGroup[]>([]);
  const [showSearchBox, setShowSearchBox] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const [isSearchResultsVisible, setSearchResultsIsVisible] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [showMoreCharactersRequired, setShowMoreCharactersRequired] = useState(false);
  const [siteName, setSiteName] = useState("");
  const [siteLanguage, setSiteLanguage] = useState("");
  const router = useRouter();

  const handleSearchInputChange = (event: any) => {
    setInputValue(event.target.value);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, 500); // Adjust the delay as needed (e.g., 500ms)

    return () => clearTimeout(timeoutId); // Clear timeout on cleanup
  }, [inputValue]);

  const callService = useCallback(async () => {
    if (debouncedValue) {
      const startsWithAlpha = /^[a-zA-Z]/.test(inputValue);
      if (
        (startsWithAlpha && inputValue.length > 2) ||
        (!startsWithAlpha && inputValue.length > 0)
      ) {
        // Valid input, fetch suggestions
        const url = "/api/search-suggestions";
        const params = {
          term: inputValue,
          datasourceId: "{BF4BC269-2552-409F-B738-8EE195735BB4}",
          site: siteName,
          language: siteLanguage
        };
        const options = {
          method: "POST",
          headers: {
            "Content-Type": "application/json; charset=utf-8"
          },
          body: JSON.stringify(params)
        };

        const res = await fetch(url, options);
        const final = await res.json();

        const searchSuggestions: SearchSuggestions = JSON.parse(final.d);
        const grouped: GroupedResults = searchSuggestions.groupedResults;
        const suggType: SuggestionType = grouped.suggestionType;
        const { groups } = suggType;

        const docResults: DocGroup[] = [];

        groups.forEach((group) => {
          const results: Document[] = [];
          const { documents } = group;
          documents.forEach((document) => {
            results.push(document);
          });
          const docGroup = { title: group.groupValue, docs: results };
          docResults.push(docGroup);
        });

        const hasResults = docResults.some((group) => group.docs.length > 0);

        setData(docResults);
        setSearchResultsIsVisible(hasResults);
        setShowMoreCharactersRequired(false);
      } else if (startsWithAlpha && inputValue.length > 0 && inputValue.length < 3) {
        setShowMoreCharactersRequired(true);
        setSearchResultsIsVisible(false);
      } else if (inputValue.length === 0) {
        setShowMoreCharactersRequired(false);
        setSearchResultsIsVisible(false);
      }
    }
  }, [debouncedValue, inputValue, siteLanguage, siteName]);

  useEffect(() => {
    callService();
  }, [callService]);

  // Create separate refs
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const moreCharsContainerRef = useRef<HTMLDivElement>(null);

  const handleCloseClick = async () => {
    if (!showSearchBox) return;

    setIsClosing(true);
    setInputValue("");
    setSearchResultsIsVisible(false);
    setShowMoreCharactersRequired(false);
    // Delay hiding the search box to allow animation to complete
    setTimeout(() => {
      setShowSearchBox(false);
      setIsClosing(false);

      // Show the phone number component again
      const phoneElement = document.querySelector(".phone-number-container") as HTMLElement;
      if (phoneElement) {
        phoneElement.style.display = "flex";
      }
    }, 125); // Match animation duration
  };

  // Update the handleClickOutside function
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is outside the search box container
      const isOutsideSearchBox = searchBoxRef.current && !searchBoxRef.current.contains(target);

      // Only check containers that actually exist (are rendered)
      const isOutsideResults =
        !resultsContainerRef.current || !resultsContainerRef.current.contains(target);
      const isOutsideMoreChars =
        !moreCharsContainerRef.current || !moreCharsContainerRef.current.contains(target);

      // Close if clicking outside the search box AND outside any visible dropdowns
      if (showSearchBox && isOutsideSearchBox && isOutsideResults && isOutsideMoreChars) {
        setSearchResultsIsVisible(false);
        setShowSearchBox(false);
        setShowMoreCharactersRequired(false);
        handleCloseClick();
      }
    },
    [showSearchBox]
  );

  // Replace your current useEffect with this:
  useEffect(() => {
    if (showSearchBox) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSearchBox, handleClickOutside]);

  useEffect(() => {
    if (showSearchBox && textFieldRef.current) {
      textFieldRef.current.focus();
    }
  }, [showSearchBox]);

  useEffect(() => {
    function IsCurrentLocale(loc: LocaleItem) {
      return loc.id === locale;
    }

    const loc: LocaleItem = localeItems.filter(IsCurrentLocale)[0];
    setSiteName(loc.siteName);
    setSiteLanguage(loc.sitecoreLanguage);
  }, [locale]);

  const getSectionBorderColor = (sectionTitle: string): string => {
    switch (sectionTitle) {
      case "SearchPage":
        return "grey.400";
      case "TourDetailPage":
        return "secondary.main";
      case "DestinationPage":
        return "#ff8102";
      case "LandingPage":
        return "#b82f41";
      case "BlogsPage":
        return "grey.400";
      case "NewsPage":
        return "secondary.main";
      default:
        return "secondary.main";
    }
  };

  const getSectionTitle = (sectionTitle: string): string => {
    switch (sectionTitle) {
      case "TourDetailPage":
        return "Tours";
      case "DestinationPage":
        return "Destinations";
      case "LandingPage":
        return "Additional Info";
      case "BlogsPage":
        return "Blog Post";
      case "NewsPage":
        return "News";
      default:
        return "";
    }
  };

  const handleOpenClick = async () => {
    setShowSearchBox(true);
    // Find and hide the phone number component
    const phoneElement = document.querySelector(".phone-number-container") as HTMLElement;
    if (phoneElement) {
      phoneElement.style.display = "none";
    }
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const startsWithAlpha = /^[a-zA-Z]/.test(inputValue);
    const isValid =
      (startsWithAlpha && inputValue.length > 2) || (!startsWithAlpha && inputValue.length > 0);

    if (e.key === "Enter") {
      e.preventDefault(); // Prevent default form submission

      if (isValid) {
        // Store the search term before clearing
        const searchTerm = inputValue;

        // Clear input and hide dropdowns immediately
        setInputValue("");
        setSearchResultsIsVisible(false);
        setShowMoreCharactersRequired(false);

        // Start closing animation
        setIsClosing(true);

        // Delay navigation and cleanup to allow animation to complete
        setTimeout(() => {
          setShowSearchBox(false);
          setIsClosing(false);

          // Show the phone number component again
          const phoneElement = document.querySelector(".phone-number-container") as HTMLElement;
          if (phoneElement) {
            phoneElement.style.display = "flex";
          }

          // Navigate to search results after animation
          router.push(
            `/find-your-tour?keyword=${encodeURIComponent(searchTerm)}&interaction=predictive_search`
          );
        }, 200); // Match animation duration
      } else {
        setShowMoreCharactersRequired(true);
        setSearchResultsIsVisible(false);
      }
    }
  };

  // Add this function to handle result clicks
  const handleResultClick = () => {
    // Clear input and hide dropdowns immediately
    setInputValue("");
    setSearchResultsIsVisible(false);
    setShowMoreCharactersRequired(false);

    // Start closing animation
    setIsClosing(true);

    // Delay cleanup to allow animation to complete
    setTimeout(() => {
      setShowSearchBox(false);
      setIsClosing(false);

      // Show the phone number component again
      const phoneElement = document.querySelector(".phone-number-container") as HTMLElement;
      if (phoneElement) {
        phoneElement.style.display = "flex";
      }
    }, 200); // Match animation duration
  };

return (
  <Stack
    direction="column"
    spacing={0}
    sx={{
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      height: "100%",
      justifyContent: "center"
    }}
  >
    {!showSearchBox &&
      <Box aria-label="open-search" className={styles.searchIconContainer} onClick={() => handleOpenClick()}>
        <Search data-testid="predictive-search-open-icon" fontSize="small" fontWeight="bold" />
      </Box>
    }
    {(showSearchBox || isClosing) && (
      <ClickAwayListener onClickAway={handleCloseClick}>
        <div>
          {(showSearchBox || isClosing) &&
            <Box
              ref={searchBoxRef} // Add this line
              className={
                isClosing
                  ? `${styles.searchBoxContainer} ${styles.searchBoxContainerClosing}`
                  : styles.searchBoxContainer
              }
            >
              <TextField
                data-testid="predictive-search-input"
                inputRef={textFieldRef}
                variant="outlined"
                onChange={(e) => handleSearchInputChange(e)}
                onKeyDown={handleSearchSubmit}
                sx={{
                  backgroundColor: "background.default",
                  width: 290,
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-root": {
                    height: 35,
                    borderRadius: "10px",
                    boxShadow: 8,
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderWidth: 0
                    }
                  },
                  "& .MuiOutlinedInput-input": {
                    fontSize: "14px",
                    padding: "8px 12px"
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "grey.100"
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "grey.100"
                  }
                }}
              />
                <Close
                  data-testid="predictive-search-close-icon"
                  className={styles.searchCloseIconContainer}
                  onClick={() => handleCloseClick()}
                  aria-label="Close Search"
                />
            </Box>
          }

        {/* Use separate conditional rendering to avoid nested ternaries */}
        {isSearchResultsVisible && !isClosing && inputValue.trim() && (
          <Box
            ref={resultsContainerRef} // Change this from containerRef
            sx={{
              position: "relative",
              zIndex: 1000,
              width: 290,
              display: "block",
              height: 0,
              overflow: "visible",
              top: "0.2rem",
              border: "1px solid",
              borderColor: "grey.100",
              boxShadow: 8,
              backgroundColor: "background.default",
              left:"50px"
            }}
          >
            <Box
              sx={{
                maxHeight: "550px",
                overflowY: "auto",
                backgroundColor: "background.default",
                "&::-webkit-scrollbar": {
                  width: "8px"
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "background.warm_strong",
                  borderRadius: "4px"
                },
                border: "1px solid",
                borderColor: "grey.100"
              }}
            >
              {Object.entries(suggestionsData).map(([sectionKey, sectionItems]) => (
                <List
                  key={`predictive-search-results-${sectionKey}`}
                  className={styles.predictiveSearchResults}
                  data-testid="predictive-search-results"
                  sx={{ backgroundColor: "background.default" }}>
                    {sectionItems.title !== "SearchPage" ? (
                      <ListItem key={sectionKey} data-testid={`predictive-search-section-title-${sectionItems.title}`}>
                        <Box className={styles.predictiveSearchTitle} >{getSectionTitle(sectionItems.title)}</Box>
                      </ListItem>
                    ):(
                      <ListItem key={sectionKey} sx={{display: "none"}} />
                    )}
                  <ListItem
                    key={`section-results-${sectionItems.title}`}
                    data-testid={`predictive-search-section-results-${sectionItems.title}`}
                    sx={{
                      padding: "0.25rem 0.75rem 0"
                    }}
                  >
                      <List key={`section-results-list-${sectionItems.title}`} sx={{padding:0, width:"100%"}}>
                        {sectionItems.docs.map((doc) => (
                          <ListItem key={doc.id} sx={{padding:0, width:"100%"}} data-testid={`predictive-search-result-item-${doc.id}`}>
                            <SearchSuggestionResultItem
                              document={doc}
                              sectionBorderColor={getSectionBorderColor(sectionItems.title)}
                              sectionTitle={sectionItems.title}
                              searchTerm={inputValue}
                              locale={locale}
                              onResultClick={handleResultClick}
                            />
                          </ListItem>
                        ))}
                      </List>
                  </ListItem>
                </List>
              ))}
            </Box>
          </Box>
          )}
          {/* Separate conditional for "more characters required" message */}
          {!isSearchResultsVisible && showMoreCharactersRequired && !isClosing && (
            <Box
              ref={moreCharsContainerRef} // Change this from containerRef
              sx={{
                position: "relative",
                zIndex: 1000,
                width: 290,
                display: "block",
                height: 0,
                overflow: "visible",
                top: "0.2rem",
                right: "4px",
                border: "1px solid",
borderColor: "grey.100",
                boxShadow: 8,
                backgroundColor: "background.default",
                left:"50px"
              }}
            >
              <Box sx={{
                maxHeight: "350px",
                overflowY: "auto",
                backgroundColor: "background.default",
                "&::-webkit-scrollbar": {
                  width: "8px"
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "background.warm_strong",
                  borderRadius: "4px"
                }
              }}
            >
              <List className={styles.predictiveSearchResults} sx={{ backgroundColor: "background.default" }}>
              <ListItem key="predictive-search-more-characters-required" data-testid="predictive-search-more-characters-required">
                <Box className={styles.genericTitle} >Please enter 3 or more characters</Box>
              </ListItem>
              </List>
            </Box>
          </Box>
          )}
        </div>
      </ClickAwayListener>
    )}
    </Stack>
  );
}
